import { z } from "zod";
import { createBaseApi } from "./base.service";
import { RESOURCES } from "./resources";
import { CreateConfigLoginDto } from "@/interfaces/zod/config-login-create";
import { LoginFormSchema } from "@/interfaces/login.interface";
import type { CampoPersonalizadoDto } from "@/interfaces/campo-personalizado.interface";
import { IPaginatedRequest } from "@/interfaces/paginated.interface";
import { UpdateLogin, AddLogin } from "@/redux/slices/auth.slice";
import { prepareImageForUpload, prepareImageFilesForUpload } from "@/lib/prepare-image-upload";
import { postLog } from "@/lib/logs";
import { skipGlobalErrorToast } from "@/lib/rtk-query-config";
import { entityListTags } from "@/lib/query-cache-tags";

export interface IgrejaParceira {
  Id: number;
  Titulo: string;
  Identificador: string;
  Slug: string;
  Identificadores: string;
  LogoId: number;
  totalEventos: number;
  logo: {
    Id: number;
    Extensao: string;
    Nome: string;
    Tamanho: number;
    Tipo: string;
    Url: string;
  };
}

export const loginApi = createBaseApi<any>(RESOURCES.LOGIN);

export const loginAPI = loginApi.injectEndpoints({
  endpoints: (build) => ({
    getLogin: build.mutation<any, null>({
      query: () => ({
        url: `/config-login`,
        method: "GET",
      }),
    }),
    getEspaco: build.query<any, null>({
      query: () => ({
        url: `/config-login/espaco-disponivel`,
        method: "GET",
      }),
      providesTags: ["Espaço"],
    }),
    postLogin: build.mutation<any, z.infer<typeof LoginFormSchema>>({
      extraOptions: skipGlobalErrorToast,
      queryFn: async (body, api, extraOptions, baseQuery) => {
        const files = await prepareImageFilesForUpload({
          logo: body.logoFile as File | undefined,
          logoEscola: body.logoEscolaFile as File | undefined,
        });
        const formData = new FormData();
        Object.keys(body).forEach((k) => {
          const value = body[k];
          if (value !== null && value !== undefined && k !== "logoFile" && k !== "logoEscolaFile" && k !== "logo" && k !== "logoEscola") {
            if (typeof value === "boolean") {
              formData.append(k, value.toString());
            } else if (typeof value === "number") {
              formData.append(k, value.toString());
            } else if (typeof value === "string") {
              formData.append(k, value);
            } else {
              formData.append(k, value);
            }
          }
        });

        if (files.logo) {
          formData.append("logo", files.logo);
        }
        if (files.logoEscola) {
          formData.append("logoEscola", files.logoEscola);
        }

        const result = await baseQuery({
            url: `/config-login`,
            method: "POST",
            body: formData,
          });
        if (result.error) {
          void postLog({
            Tipo: "Arquivos_Falhou_Upload",
            Objeto: {
              acao: "upload",
              contexto: "post_login",
              status: result.error.status,
            },
          });
        }
        return result;
      },
      async onQueryStarted(_query, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        dispatch(UpdateLogin(data));
      },
      invalidatesTags: (_r, error, body) => error ? [] : [
        { type: "IgrejasParceiras", id: "LIST" },
        { type: RESOURCES.LOGIN, id: "LIST" },
        ...(body.Id ? [{ type: "IgrejasParceiras", id: `perfil-${body.Id}` }] : []),
      ],
    }),
    getLoginByIdentificador: build.query<any, string>({
      query: (login) => ({
        url: `/config-login/byIdentificador`,
        params: {
          login,
        },
        method: "GET",
      }),
    }),
    getLoginBySlug: build.query<any, string>({
      query: (slug) => ({
        url: `/config-login/bySlug`,
        params: {
          slug,
        },
        method: "GET",
      }),
    }),
    getOrganizacoesMobile: build.query<
      Array<{
        id: number;
        titulo: string;
        slug: string | null;
        identificador: string | null;
        logoUrl: string | null;
        totalUsuarios?: number;
      }>,
      void
    >({
      query: () => ({
        url: `/config-login/organizacoes-mobile`,
        method: "GET",
      }),
    }),
    /** Publico (pagina de precos): Id, Titulo, Slug, logo e totalEventos. */
    getParceirosPublico: build.query<
      Array<{ Id: number; Titulo: string; Slug: string | null; logo: { Id: number; Extensao: string } | null; totalEventos: number }>,
      null
    >({
      query: () => ({
        url: `/config-login/parceiros-publico`,
        method: "GET",
      }),
    }),
    getIgrejasParceiras: build.query<any, null>({
      query: () => ({
        url: `/config-login`,
        method: "GET",
      }),
      providesTags: (result) => entityListTags("IgrejasParceiras", result),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalidate the client-side projection only AFTER its source has refreshed.
          // Invalidating both at once lets the projection read the previous source cache.
          dispatch(loginAPI.util.invalidateTags([{ type: "IgrejasParceiras", id: "TABLE" }]));
        } catch { /* Keep the last successful table on a transient source error. */ }
      },
    }),
    /** Lista para DataTable do backoffice: reutiliza cache de getIgrejasParceiras e pagina no cliente. */
    getBackofficeOrganizacoesTable: build.query<
      { data: any[]; meta: { total: number }; totalWithoutFilters: number },
      IPaginatedRequest
    >({
      async queryFn(arg, { dispatch, getState }) {
        // Tipos explicitos: sem eles o retorno do queryFn depende do proprio loginAPI (auto-referencia)
        // e o TS 7 infere loginAPI inteiro como any (os hooks viram UseQuery<any> com data unknown).
        const readCache = (): any[] => {
          const state = getState() as any;
          const raw = loginAPI.endpoints.getIgrejasParceiras.select(null)(state)?.data;
          return Array.isArray(raw) ? raw : [];
        };

        let all: any[];
        {
          try {
            const result = await dispatch(
              loginAPI.endpoints.getIgrejasParceiras.initiate(null, {
                subscribe: false,
              }),
            ).unwrap();
            all = Array.isArray(result) ? result : readCache();
          } catch (error) {
            return {
              error: {
                status: "FETCH_ERROR" as const,
                error: error instanceof Error ? error.message : String(error),
              },
            };
          }
        }
        const q = (arg.search ?? "").trim().toLowerCase();
        let filtered = all;
        if (q) {
          const matchesText = (org: any) =>
            (org.Titulo ?? "").toLowerCase().includes(q) ||
            (org.Slug ?? "").toLowerCase().includes(q) ||
            (org.Identificador ?? "").toLowerCase().includes(q) ||
            (org.Identificadores ?? "").toLowerCase().includes(q) ||
            (org.TituloEscola ?? "").toLowerCase().includes(q) ||
            (org.IdentificadorEscola ?? "").toLowerCase().includes(q) ||
            (org.ResponsavelTecnicoNome ?? "").toLowerCase().includes(q) ||
            (org.ResponsavelTecnicoTelefone ?? "").toLowerCase().includes(q) ||
            (org.ResponsavelFinanceiroNome ?? "").toLowerCase().includes(q) ||
            (org.ResponsavelFinanceiroTelefone ?? "").toLowerCase().includes(q) ||
            (org.PastorNome ?? "").toLowerCase().includes(q) ||
            (org.PastorTelefone ?? "").toLowerCase().includes(q) ||
            (org.ResponsavelTecnicoEscolaNome ?? "").toLowerCase().includes(q) ||
            (org.ResponsavelTecnicoEscolaTelefone ?? "").toLowerCase().includes(q) ||
            (org.ResponsavelFinanceiroEscolaNome ?? "").toLowerCase().includes(q) ||
            (org.ResponsavelFinanceiroEscolaTelefone ?? "").toLowerCase().includes(q) ||
            (org.PastorEscolaNome ?? "").toLowerCase().includes(q) ||
            (org.PastorEscolaTelefone ?? "").toLowerCase().includes(q) ||
            (org.Endereco ?? "").toLowerCase().includes(q);
          const matchesNum = (v: unknown) =>
            v != null && v !== "" && String(v).toLowerCase().includes(q);
          filtered = all.filter(
            (org: any) =>
              matchesText(org) ||
              matchesNum(org.Mensalidade) ||
              matchesNum(org.MensalidadeEscola) ||
              matchesNum(org.DiaVencimentoMensalidade) ||
              matchesNum(org.Taxa) ||
              matchesNum(org.totalUsuarios),
          );
        }
        const dir = (arg.sortingDir ?? "asc").toLowerCase() === "desc" ? -1 : 1;
        const sortField = arg.sortingField;
        const sortNum = (a: any, b: any, key: string) => {
          const na = a[key] != null && a[key] !== "" ? Number(a[key]) : NaN;
          const nb = b[key] != null && b[key] !== "" ? Number(b[key]) : NaN;
          const va = Number.isFinite(na) ? na : 0;
          const vb = Number.isFinite(nb) ? nb : 0;
          return dir * (va - vb);
        };
        let sorted = filtered;
        if (sortField) {
          sorted = [...filtered].sort((a: any, b: any) => {
            if (sortField === "organizacao") {
              return (
                dir *
                String(a.Titulo ?? "").localeCompare(String(b.Titulo ?? ""), "pt-BR", {
                  sensitivity: "base",
                })
              );
            }
            if (sortField === "dominio") {
              return (
                dir *
                String(a.Slug ?? "").localeCompare(String(b.Slug ?? ""), "pt-BR", {
                  sensitivity: "base",
                })
              );
            }
            if (sortField === "dataCadastro") {
              const ta = a.DataCadastro != null && a.DataCadastro !== "" ? new Date(a.DataCadastro).getTime() : 0;
              const tb = b.DataCadastro != null && b.DataCadastro !== "" ? new Date(b.DataCadastro).getTime() : 0;
              const va = Number.isFinite(ta) ? ta : 0;
              const vb = Number.isFinite(tb) ? tb : 0;
              return dir * (va - vb);
            }
            if (sortField === "totalUsuarios") return sortNum(a, b, "totalUsuarios");
            if (sortField === "mensalidade") return sortNum(a, b, "Mensalidade");
            if (sortField === "diaVencimentoMensalidade") {
              return sortNum(a, b, "DiaVencimentoMensalidade");
            }
            if (sortField === "responsavelTecnico") {
              const sa = [
                `${a.ResponsavelTecnicoNome ?? ""} ${a.ResponsavelTecnicoTelefone ?? ""}`.trim(),
                `${a.ResponsavelTecnicoEscolaNome ?? ""} ${a.ResponsavelTecnicoEscolaTelefone ?? ""}`.trim(),
              ]
                .filter(Boolean)
                .join(" ");
              const sb = [
                `${b.ResponsavelTecnicoNome ?? ""} ${b.ResponsavelTecnicoTelefone ?? ""}`.trim(),
                `${b.ResponsavelTecnicoEscolaNome ?? ""} ${b.ResponsavelTecnicoEscolaTelefone ?? ""}`.trim(),
              ]
                .filter(Boolean)
                .join(" ");
              return dir * sa.localeCompare(sb, "pt-BR", { sensitivity: "base" });
            }
            if (sortField === "responsavelFinanceiro") {
              const sa = [
                `${a.ResponsavelFinanceiroNome ?? ""} ${a.ResponsavelFinanceiroTelefone ?? ""}`.trim(),
                `${a.ResponsavelFinanceiroEscolaNome ?? ""} ${a.ResponsavelFinanceiroEscolaTelefone ?? ""}`.trim(),
              ]
                .filter(Boolean)
                .join(" ");
              const sb = [
                `${b.ResponsavelFinanceiroNome ?? ""} ${b.ResponsavelFinanceiroTelefone ?? ""}`.trim(),
                `${b.ResponsavelFinanceiroEscolaNome ?? ""} ${b.ResponsavelFinanceiroEscolaTelefone ?? ""}`.trim(),
              ]
                .filter(Boolean)
                .join(" ");
              return dir * sa.localeCompare(sb, "pt-BR", { sensitivity: "base" });
            }
            if (sortField === "pastor") {
              const sa = [
                `${a.PastorNome ?? ""} ${a.PastorTelefone ?? ""}`.trim(),
                `${a.PastorEscolaNome ?? ""} ${a.PastorEscolaTelefone ?? ""}`.trim(),
              ]
                .filter(Boolean)
                .join(" ");
              const sb = [
                `${b.PastorNome ?? ""} ${b.PastorTelefone ?? ""}`.trim(),
                `${b.PastorEscolaNome ?? ""} ${b.PastorEscolaTelefone ?? ""}`.trim(),
              ]
                .filter(Boolean)
                .join(" ");
              return dir * sa.localeCompare(sb, "pt-BR", { sensitivity: "base" });
            }
            if (sortField === "endereco") {
              return (
                dir *
                String(a.Endereco ?? "").localeCompare(String(b.Endereco ?? ""), "pt-BR", {
                  sensitivity: "base",
                })
              );
            }
            if (sortField === "taxa") {
              return sortNum(a, b, "Taxa");
            }
            if (sortField === "ativo") {
              return dir * ((a.Status ?? 1) - (b.Status ?? 1));
            }
            return 0;
          });
        }
        const page = arg.page ?? 1;
        const perPage = arg.perPage ?? 50;
        const total = sorted.length;
        const start = (page - 1) * perPage;
        const slice = sorted.slice(start, start + perPage);
        return {
          data: {
            data: slice,
            meta: { total },
            totalWithoutFilters: all.length,
          },
        };
      },
      providesTags: [{ type: "IgrejasParceiras", id: "TABLE" }],
      async onCacheEntryAdded(_arg, { dispatch, cacheEntryRemoved }) {
        const source = dispatch(loginAPI.endpoints.getIgrejasParceiras.initiate(null));
        try { await cacheEntryRemoved; } finally { source.unsubscribe(); }
      },
    }),
    createConfigLogin: build.mutation<
      any,
      CreateConfigLoginDto & {
        personalizados?: Array<{ Id: number; Tipo: string; Valor: unknown }>;
      }
    >({
      extraOptions: skipGlobalErrorToast,
      queryFn: async (body, api, extraOptions, baseQuery) => {
        const hasLogoFile = body.logoFile instanceof File;
        const personalizadosJson =
          body.personalizados != null ? JSON.stringify(body.personalizados) : undefined;
        if (hasLogoFile) {
          const preparedLogo = await prepareImageForUpload(body.logoFile as File);
          const formData = new FormData();
          Object.entries(body).forEach(([k, value]) => {
            if (value === undefined || value === null || k === "logoFile" || k === "personalizados") return;
            if (typeof value === "boolean") formData.append(k, value.toString());
            else if (typeof value === "number") formData.append(k, value.toString());
            else if (typeof value === "string") formData.append(k, value);
          });
          formData.append("logo", preparedLogo);
          if (personalizadosJson) formData.append("personalizados", personalizadosJson);
          const result = await baseQuery({
            url: `/config-login`,
            method: "POST",
            body: formData,
          });
          if (result.error) {
            void postLog({
              Tipo: "Arquivos_Falhou_Upload",
              Objeto: {
                acao: "upload",
                contexto: "create_config_login",
                status: result.error.status,
              },
            });
          }
          return result;
        }
        const { logoFile: _, personalizados, ...jsonBody } = body;
        return baseQuery({
            url: `/config-login`,
            method: "POST",
            body: personalizados != null ? { ...jsonBody, personalizados } : jsonBody,
          });
      },
      invalidatesTags: (_r, error, body) => error ? [] : [
        { type: "IgrejasParceiras", id: "LIST" },
        { type: RESOURCES.LOGIN, id: "LIST" },
        ...(body.Id ? [{ type: "IgrejasParceiras", id: `perfil-${body.Id}` }] : []),
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data?.Id) {
            if (arg.Id) {
              dispatch(UpdateLogin(data));
            } else {
              dispatch(AddLogin(data));
            }
          }
        } catch {
          // Ignore - erro já tratado pelo toast
        }
      },
    }),
    getPerfilOrganizacao: build.query<
      {
        Organizacao: Record<string, unknown>;
        CamposPersonalizados: CampoPersonalizadoDto[];
      },
      number
    >({
      query: (orgId) => ({
        url: `/config-login/${orgId}/perfil-organizacao`,
        method: "GET",
      }),
      providesTags: (_r, _e, orgId) => [{ type: "IgrejasParceiras", id: `perfil-${orgId}` }],
    }),
    patchOrganizacaoCamposPersonalizados: build.mutation<
      unknown,
      { orgId: number; personalizados: Array<{ Id: number; Tipo: string; Valor: unknown }> }
    >({
      query: ({ orgId, personalizados }) => ({
        url: `/config-login/${orgId}/campos-personalizados`,
        method: "PATCH",
        body: { personalizados },
      }),
      invalidatesTags: (_r, _e, { orgId }) => [
        { type: "IgrejasParceiras", id: `perfil-${orgId}` },
        { type: "IgrejasParceiras", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetLoginMutation,
  useGetLoginByIdentificadorQuery,
  useGetLoginBySlugQuery,
  usePostLoginMutation,
  useGetEspacoQuery,
  useGetIgrejasParceirasQuery,
  useGetParceirosPublicoQuery,
  useGetOrganizacoesMobileQuery,
  useGetBackofficeOrganizacoesTableQuery,
  useCreateConfigLoginMutation,
  useGetPerfilOrganizacaoQuery,
  usePatchOrganizacaoCamposPersonalizadosMutation,
} = loginAPI;
